// Iter46 — Admin Phase 2 index page (6 cards). Tenant-admin + platform_admin
// gated by RouteGuard at the route level.
import React from "react";
import { Link, useLocation, Outlet } from "react-router-dom";
import { Users, Clock, ArrowUpRight, FileText, Wrench, UserCheck, Building2, Tag, Network, Truck, ShieldCheck, Crown, Sparkles, GitBranch, Repeat2, Target, Coins, ScrollText, Boxes, CalendarClock } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { usePendingUsers } from "../api/sitroom";

const mono = (size, color, weight, ls) => ({
  fontFamily: "DM Mono, monospace",
  fontSize: size,
  color: color || "var(--text, #f0f2f8)",
  fontWeight: weight || 400,
  letterSpacing: ls,
});

const CARDS = [
  { to: "/admin/users",                icon: Users,        title: "Users & Roles",          desc: "Invite users · platform admin manages all accounts", testId: "admin-card-users" },
  { to: "/admin/approvals",            icon: UserCheck,    title: "Pending Approvals",      desc: "Approve or reject user invitations", testId: "admin-card-approvals", showBadge: true },
  { to: "/admin/sla",                  icon: Clock,        title: "SLA Rules",              desc: "Severity × tenant-pair overrides for ack + resolution", testId: "admin-card-sla" },
  { to: "/admin/escalation",           icon: ArrowUpRight, title: "Escalation Chains",      desc: "Notification ladders per severity + tenant", testId: "admin-card-escalation" },
  { to: "/admin/templates",            icon: FileText,     title: "Incident Templates",     desc: "Versioned §3.1 templates with default severity, SLA tier, mandatory fields", testId: "admin-card-templates" },
  { to: "/admin/incident-categories",  icon: Tag,          title: "Incident Categories",    desc: "Localised §3.1 categories — default severity, originating + auto-linked party types", testId: "admin-card-incident-categories" },
  { to: "/admin/tenant-types",         icon: Building2,    title: "Tenant Types",           desc: "18 system types across 5 supply-chain layers (§2.4)", testId: "admin-card-tenant-types" },
  { to: "/admin/tenants",              icon: Network,      title: "Tenants & Relationships", desc: "Tenant CRUD · contacts · directed supply-chain links · part responsibility", testId: "admin-card-tenants" },
  { to: "/admin/sr-types",             icon: Wrench,       title: "Service Request Types",  desc: "Master CRUD with EN/DE/HU labels + handling module + assignee category", testId: "admin-card-sr-types" },
  { to: "/admin/logistics-vehicle-types", icon: Truck,      title: "Logistics · Vehicle Types", desc: "Autótípus master list — admin-managed dropdown with EN/DE/HU labels + transport mode", testId: "admin-card-logistics-vtypes" },
  { to: "/admin/logistics-approval-rules", icon: ShieldCheck, title: "Logistics · Approval Rules", desc: "Amount + complexity thresholds — block to_send when triggered", testId: "admin-card-logistics-approvals" },
  { to: "/admin/logistics-categories",  icon: Crown,        title: "Logistics · Customer Categories", desc: "Mark categories as Key Account · drives Key Account Dashboard", testId: "admin-card-logistics-categories" },
  // γ-3-I — Pilot observability for the SITREP AI intake. Platform-admin only.
  { to: "/admin/ai-usage",              icon: Sparkles,     title: "AI Usage · pilot",        desc: "Total calls · per user · per tenant · per context · per day. Observability only — no quotas.", testId: "admin-card-ai-usage" },
  // Phase A — Customer portal backbone. Platform-admin manages customer/carrier tenants.
  { to: "/admin/customer-relationships", icon: GitBranch,    title: "Customer Relationships", desc: "Phase A · Link forwarder ↔ customer/carrier tenants · flip tenant_type · seed primary users", testId: "admin-card-customer-relationships" },
  // Phase F.1 — Forwarder-owned customer master-data + contacts (payment terms, billing address, contacts).
  { to: "/admin/customer-masters", icon: Users, title: "Customer Masters", desc: "Phase F · Payment terms · billing address · contacts (logistics / commercial / accounting) · used by send-out routing", testId: "admin-card-customer-masters" },
  // G.4 — Recurring freight scheduler. Template + schedules (daily/weekly/specific-dates/cron) → auto-generated child deals.
  { to: "/admin/recurring-series", icon: Repeat2, title: "Recurring Freight Series", desc: "G.4 · Template + schedule → auto-generated freight deals. Pause/stop without losing history. Children link via existing trip-links panel.", testId: "admin-card-recurring-series" },
  // H.5 — Planning input + planned-vs-actual variance overlay on the BI dashboard.
  { to: "/admin/bi-plans", icon: Target, title: "BI Plans (per customer × month)", desc: "H.5 · Platform-admin sets plan revenue/count/margin per customer per month. Drives variance pills on the BI dashboard.", testId: "admin-card-bi-plans" },
  // H additions — Monthly EUR/HUF FX rates for currency-toggle conversion.
  { to: "/admin/bi-fx-rates", icon: Coins, title: "BI FX Rates (EUR ↔ HUF)", desc: "Platform-admin enters monthly EUR/HUF rates. Used to convert BI dashboards when switching display currency.", testId: "admin-card-bi-fx-rates" },
  // I.3 — Contract rate cards + zero-tolerance deviation gate.
  { to: "/admin/contract-rates", icon: ScrollText, title: "Contract Rates", desc: "I.3 · Rate per customer + directional pickup/dropoff postal + vehicle + load type. Zero tolerance — any price deviation force-routes the deal to approval with mandatory justification.", testId: "admin-card-contract-rates" },
  // J.2 — Frame / Blanket orders + call-off drawdown.
  { to: "/admin/frame-orders", icon: Boxes, title: "Frame / Blanket Orders", desc: "J.2 · Caps on freight count and/or total amount per customer. Call-offs draw down live; warn at 90 %, force-approval on exceed (default).", testId: "admin-card-frame-orders" },
  // J.5b — Per-route minimum-margin overrides.
  { to: "/admin/min-margin-lanes", icon: Target, title: "Min-Margin · Per-Route", desc: "J.5b · Lane-specific min-margin% (postal + opt vehicle + opt mode) that overrides the per-customer default. Below threshold → force-approval via the J.1 router.", testId: "admin-card-min-margin-lanes" },
  // Időkapu-foglaló · Part 1 — factory setup for Dock Time-Slot Booking (Gyári tenant admin).
  { to: "/admin/dock-scheduling", icon: CalendarClock, title: "Időkapu-foglaló · Beállítások", desc: "Gyári tenant · dokkok, kapuk, fogadási idősávok, foglalási mezők — Part 1 (setup only).", testId: "admin-card-dock-scheduling", gate: "dock_admin" },
];

export default function AdminIndex() {
  const loc = useLocation();
  const { user } = useAuth();
  const onIndex = loc.pathname === "/admin" || loc.pathname === "/admin/";
  const { data: pending = [] } = usePendingUsers(Boolean(user));
  // Időkapu-foglaló · Part 1 — filter cards by dock gate. Only show the
  // dock-scheduling admin card when the tenant flag is on AND the caller has
  // the dock_admin role (platform_admin always sees it).
  const dockAdminOk = user?.role === "platform_admin"
    || (user?.dock_scheduling_enabled && (user?.dock_roles || []).includes("dock_admin"));
  const cards = CARDS.filter((c) => {
    if (c.gate === "dock_admin") return dockAdminOk;
    return true;
  });
  return (
    <div data-testid="admin-index">
      <header className="mb-5">
        <h1 style={{ fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 800, fontSize: "clamp(26px, 3.4vw, 34px)", color: "var(--text, #f0f2f8)", letterSpacing: "-0.5px" }} data-testid="view-title">
          Admin
        </h1>
        <div className="mt-1.5" style={mono(11, "var(--text-dim, #c5cad8)")}>
          Tenant configuration · governance · access control
        </div>
      </header>

      {/* Quick chip nav — visible on every sub-route */}
      <div className="flex flex-wrap gap-2 mb-5" data-testid="admin-chip-nav">
        {cards.map((c) => (
          <Link
            key={c.to}
            to={c.to}
            className="px-3 py-1.5 rounded"
            style={{
              background: loc.pathname.startsWith(c.to) ? "rgba(24,95,165,0.18)" : "transparent",
              border: `1px solid ${loc.pathname.startsWith(c.to) ? "rgba(24,95,165,0.55)" : "rgba(255,255,255,0.12)"}`,
              color: loc.pathname.startsWith(c.to) ? "#9ec3ec" : "var(--text-dim, #c5cad8)",
              ...mono(10, undefined, 700, "0.4px"),
              textTransform: "uppercase",
              textDecoration: "none",
            }}
            data-testid={`admin-chip-${c.to.split("/").pop()}`}
          >{c.title}</Link>
        ))}
      </div>

      {onIndex ? (
        <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" data-testid="admin-cards-grid">
          {cards.map((c) => (
            <Link
              key={c.to}
              to={c.to}
              data-testid={c.testId}
              className="p-4 rounded-md hover:translate-y-[-1px] transition-transform"
              style={{
                background: "var(--bg-surface, #0d0f18)",
                border: "1px solid rgba(255,255,255,0.10)",
                textDecoration: "none",
              }}
            >
              <div className="flex items-center gap-2 mb-2">
                <c.icon size={18} color="var(--blue, #185FA5)" />
                <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 14, color: "var(--text, #f0f2f8)" }}>
                  {c.title}
                </div>
                {c.showBadge && pending.length > 0 && (
                  <span
                    data-testid="admin-card-approvals-badge"
                    style={{
                      background: "rgba(243,177,74,0.20)", color: "#f3b14a",
                      padding: "1px 7px", borderRadius: 10,
                      ...mono(9, undefined, 700, "0.4px"),
                      marginLeft: "auto",
                    }}
                  >{pending.length}</span>
                )}
              </div>
              <div style={mono(11, "var(--dimmer, #9198b0)")}>{c.desc}</div>
            </Link>
          ))}
        </div>
      ) : (
        <Outlet />
      )}
    </div>
  );
}
